/**
 * Process-wide cache of Howl instances, keyed by source + Howl options.
 *
 * WHY THIS EXISTS — table-switch cost. Switching tables remounts the whole
 * per-table subtree (the providers in pages/Game.tsx are keyed by gameID), and
 * every sound-owning component in it used to construct its OWN `new Howl(...)`
 * on mount and unload it on unmount. Per table that is roughly:
 *
 *   SeatCards 1 + SeatTimer 2 + Bet 2  per seated seat   (≈45 at a full table)
 *   + 6 at the page level (pages/Game.tsx)
 *
 * i.e. ~50 Howl constructions — each one an XHR/decode plus Web Audio node
 * setup — paid on every single switch, and briefly doubled while the carousel
 * has both tables mounted. That work lands in exactly the frames the switch
 * needs, which is why suppressing the replayed GSAP animations alone did not
 * fix the frame drops.
 *
 * With this cache a remount is a Map lookup: the audio for a given
 * (src + options) pair is constructed ONCE per session and shared by every
 * component that asks for it.
 *
 * DELIBERATELY NEVER UNLOADED. The old per-mount instances were unloaded on
 * unmount, which is what made construction repeat; a heap diff of a 4-table
 * replay showed 483 Howls created and 0 actually freed, so the "cleanup" wasn't
 * even holding. Retaining one instance per distinct sound (about a dozen files)
 * is bounded and far cheaper than re-creating dozens per switch.
 *
 * Sharing is safe because volume/rate are part of the cache key (two call sites
 * with different volumes get different instances), Howler supports overlapping
 * `play()` calls on one instance, and the only caller that uses `stop()` (the
 * app-level waitlist alert) is mounted once.
 */

import type { Howl as HowlInstance } from 'howler';

type HowlVars = Record<string, unknown>;

type CacheEntry = {
  /** Constructed instance; null while howler is still being imported. */
  howl: HowlInstance | null;
  /** Callbacks waiting for the in-flight construction of this key. */
  waiters: Array<(howl: HowlInstance) => void>;
};

const cache = new Map<string, CacheEntry>();

/** Howler is imported lazily (and once) — sounds can't play during load anyway. */
let howlerPromise: Promise<any> | null = null;

const loadHowlConstructor = (): Promise<any> => {
  if (!howlerPromise) {
    howlerPromise = import('howler').then((mod: any) => mod?.Howl ?? mod?.default?.Howl ?? null);
  }
  return howlerPromise;
};

/**
 * Cache identity for a sound: its source(s) plus every option that affects the
 * constructed instance (volume, rate, sprite, …). Exported for unit testing.
 */
export const soundCacheKey = (src: string | string[], vars: HowlVars): string =>
  JSON.stringify([Array.isArray(src) ? src : [src], vars]);

/**
 * Hand the caller the shared Howl for this (src, vars) pair, constructing it on
 * first request. Returns a cancel function so a consumer that unmounts before
 * howler finishes loading is not called back (the instance still lands in the
 * cache for the next consumer).
 */
export const acquireSound = (
  src: string | string[],
  vars: HowlVars,
  onReady: (howl: HowlInstance) => void
): (() => void) => {
  const key = soundCacheKey(src, vars);
  let cancelled = false;
  const cancel = () => {
    cancelled = true;
  };
  const deliver = (howl: HowlInstance) => {
    if (!cancelled) onReady(howl);
  };

  const existing = cache.get(key);
  if (existing?.howl) {
    deliver(existing.howl);
    return cancel;
  }
  if (existing) {
    existing.waiters.push(deliver);
    return cancel;
  }

  const entry: CacheEntry = { howl: null, waiters: [deliver] };
  cache.set(key, entry);

  loadHowlConstructor()
    .then((Howl: any) => {
      if (!Howl) {
        cache.delete(key);
        return;
      }
      const howl: HowlInstance = new Howl({
        src: Array.isArray(src) ? src : [src],
        ...vars
      });
      entry.howl = howl;
      const waiters = entry.waiters;
      entry.waiters = [];
      waiters.forEach((waiter) => waiter(howl));
    })
    .catch(() => {
      // howler unavailable (offline chunk, blocked import) — drop the entry so a
      // later mount can retry rather than waiting on a promise that never lands.
      cache.delete(key);
    });

  return cancel;
};

/** Number of distinct sounds currently retained. Diagnostics + tests. */
export const cachedSoundCount = (): number => cache.size;

/** Test-only reset of the module-level cache. */
export const __resetSoundCache = (): void => {
  cache.clear();
  howlerPromise = null;
};
